import { useState } from 'react';
import { useNavigate } from 'react-router';
import { FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	ICreateNewRenewalVersionResponse,
	setNewFutureDatedRenewalPolicyNumber,
} from '@acorn-portal/shared/src/store/renewal.slice';
import { PaymentClient } from '@acorn-portal/shared/src/services/lookupApi';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import {
	PaymentOption,
	PaymentIntentResponse,
	SavedPaymentMethodDetails,
} from '@acorn-portal/shared/src/services/renewalsApi';
import { IRenewalMakePaymentForm } from '@acorn-portal/shared/src/pages/renewal/MakePayment';
import useStripeRenewalPayment from '@acorn-portal/shared/src/hooks/useStripeRenewalPayment';
import useBraintreeRenewalPayment from '@acorn-portal/shared/src/hooks/useBraintreeRenewalPayment';
import { useBraintreeContext } from '@acorn-portal/shared/src/context/braintree-context';
import { PaymentMethodPayload } from 'braintree-web-drop-in';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import { Product } from '../services/renewalsApiV2';
import { ExtendPaymentMethodOptions } from '../utils/types/extensions/braintree';
import { Address } from '../services/api';
import { hasErrorCode, handleErrorCodeExceptions } from '../utils/handle-error-codes-exceptions';

const useHandleRenewalPayment = (
	requiresPayment: boolean,
	useExistingCard: boolean | undefined,
	renewal: ICreateNewRenewalVersionResponse,
	requiredDirectDebitInfo: SavedPaymentMethodDetails | undefined,
) => {
	const policy = useAppSelector((state) => state.fullPolicy);
	const contactPreferences = useAppSelector((redux) => redux.contactPreferences);
	const { instance } = useBraintreeContext();

	const stripe = useStripe();
	const elements = useElements();

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [problem, setProblem] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const { getPaymentIntent, takeUpRenewal, confirmPayment } = useStripeRenewalPayment({
		policy,
		renewal,
		stripe,
		elements,
	});

	const { getPaymentIntent: getPaymentIntentBraintree, takeUpRenewal: takeUpRenewalBraintree } =
		useBraintreeRenewalPayment({
			policy,
			renewal,
		});

	const handleCardHolderAddress = (address: Address): Address => {
		const line1Check = address.line1;
		if (line1Check.length > 50) {
			const line1 = line1Check.split(',')[0];
			const line2 =
				line1Check.split(',')[1].indexOf(' ') == 0 ? line1Check.split(',')[1].substring(1) : line1Check.split(',')[1];
			const line3 = address.line2;

			return {
				...address,
				line1,
				line2,
				line3,
			};
		}

		return address;
	};

	const handleStripePaymentSubmit = async (
		values: IRenewalMakePaymentForm,
		helpers: FormikHelpers<IRenewalMakePaymentForm>,
	) => {
		setSubmitting(true);

		const paymentOption = getPaymentOption();

		const amount = getPaymentAmount(paymentOption);

		try {
			const newDirectDebitDetails = await validateAndFetchBankDetails(values, helpers);

			let paymentIntent: PaymentIntentResponse | null = null;
			if (requiresPayment && !useExistingCard && amount) {
				paymentIntent = await getPaymentIntent(amount, values.saveCard, {
					...values.address,
					postalCode: values.address.postcode,
					country: 'UK',
				});
			}

			const stripeResponse = paymentIntent ? await confirmPayment(values, paymentIntent, useExistingCard) : null;

			if (paymentIntent && !stripeResponse?.paymentIntent?.id) {
				throw new Error('Payment confirmation failed');
			}

			const takeup = await takeUpRenewal(
				getPaymentOption(),
				renewal.isFutureDatedRenewal,
				contactPreferences.printToPost,
				renewal.futureRenewalStartDate,
				stripeResponse?.paymentIntent?.id,
				paymentIntent?.paymentId,
				requiredDirectDebitInfo,
				newDirectDebitDetails,
				values.id,
			);

			if (renewal.isFutureDatedRenewal) {
				dispatch(setNewFutureDatedRenewalPolicyNumber(takeup.policyReference));
			}

			navigate(ROUTE_PATHS.renewalCheckoutSuccess);
		} catch (error) {
			console.error('Payment error:', error);
			setProblem(true);
		} finally {
			setSubmitting(false);
		}
	};

	const handleBraintreePaymentProblem = async () => {
		setProblem(true);
		instance?.clearSelectedPaymentMethod();
	};

	const handleBraintreePaymentSubmit = async (
		values: IRenewalMakePaymentForm,
		helpers: FormikHelpers<IRenewalMakePaymentForm>,
	) => {
		setSubmitting(true);
		setError(null);

		const paymentOption = getPaymentOption();

		const amount = getPaymentAmount(paymentOption);

		try {
			const newDirectDebitDetails = await validateAndFetchBankDetails(values, helpers);

			let braintreeResponse: PaymentMethodPayload | undefined;
			let paymentIntent: PaymentIntentResponse | null = null;

			if (requiresPayment && !useExistingCard && amount) {
				if (!instance) {
					setProblem(true);
					setSubmitting(false);
					return;
				}

				paymentIntent = await getPaymentIntentBraintree(amount, values.saveCard, {
					...values.address,
					postalCode: values.address.postcode,
					country: 'UK',
				});

				if (!paymentIntent) {
					setProblem(true);
					setSubmitting(false);
					return;
				}

				const fixedAddress = handleCardHolderAddress(values.address);

				const requestPayload: ExtendPaymentMethodOptions = {
					threeDSecure: {
						amount: amount.toString(),
						challengeRequested: values.saveCard,
						email: values.cardholderEmail,
						billingAddress: {
							givenName: values.cardholderFirstName,
							surname: values.cardholderLastName,
							streetAddress: fixedAddress.line1,
							extendedAddress: fixedAddress.line2 ?? undefined,
							line3: fixedAddress.line3 ?? undefined,
							locality: fixedAddress.city ?? undefined,
							postalCode: fixedAddress.postcode,
						},
						additionalInformation: {
							ipAddress: paymentIntent.clientIp,
						},
					},
				};
				braintreeResponse = await instance.requestPaymentMethod(requestPayload);

				if (!braintreeResponse) {
					return handleBraintreePaymentProblem();
				}

				if (!braintreeResponse.deviceData) {
					return handleBraintreePaymentProblem();
				}

				if (
					braintreeResponse.type === 'CreditCard' &&
					(!braintreeResponse.threeDSecureInfo?.liabilityShifted ||
						!braintreeResponse.threeDSecureInfo?.liabilityShiftPossible)
				) {
					return handleBraintreePaymentProblem();
				}
			}

			const takeup = await takeUpRenewalBraintree({
				paymentOption: paymentOption,
				isFutureStartDate: renewal.isFutureDatedRenewal,
				startDate: renewal.renewalStartDate,
				paymentId: paymentIntent?.paymentId,
				idempotencyKey: paymentIntent?.idempotencyKey,
				braintreePaymentDetails: braintreeResponse,
				balanceSettlementDetails: requiredDirectDebitInfo,
				newBalanceSettlementDetails: newDirectDebitDetails,
				existingCardPaymentMethodId: values.id,
				sendPostalDocuments: contactPreferences.printToPost,
				product: policy.product as Product,
			});

			if (renewal.isFutureDatedRenewal) {
				dispatch(setNewFutureDatedRenewalPolicyNumber(takeup.policyReference));
			}

			navigate(ROUTE_PATHS.renewalCheckoutSuccess);
		} catch (error) {
			if (hasErrorCode(error)) {
				const paymentError = handleErrorCodeExceptions(error.response, navigate);
				if (paymentError) {
					setError(paymentError.message);
					instance?.clearSelectedPaymentMethod();
				}
			} else {
				setProblem(true);
			}
		} finally {
			setSubmitting(false);
		}
	};

	const validateAndFetchBankDetails = async (
		values: IRenewalMakePaymentForm,
		helpers: FormikHelpers<IRenewalMakePaymentForm>,
	) => {
		if (values.new_direct_debit && values.sort_code && values.account_number) {
			const client = new PaymentClient(undefined, API);
			const checkBank = await client.checkBankDetails({
				sortCode: values.sort_code,
				accountNumber: values.account_number,
			});

			if (!checkBank.isValid) {
				helpers.setFieldError('account_number', 'Bank Details are invalid');
				helpers.setFieldError('sort_code', 'Bank Details are invalid');

				document.getElementById('sort_code_field')?.scrollIntoView({ behavior: 'smooth', block: 'center' });

				throw new Error('Invalid bank details');
			}

			return {
				sortCode: values.sort_code,
				accountNumber: values.account_number,
				bankName: checkBank.bankName,
			};
		}
		return undefined;
	};

	const getPaymentOption = (): PaymentOption => {
		if (renewal.paymentOptions.method === 'finance') {
			return renewal.paymentOptions.deposit ? PaymentOption.FinanceWithDeposit : PaymentOption.FinanceWithoutDeposit;
		}
		return PaymentOption.PayInFull;
	};

	const getPaymentAmount = (paymentOption: PaymentOption) => {
		switch (paymentOption) {
			case PaymentOption.FinanceWithDeposit:
				return renewal.renewalRatingResponse?.financePlanWithDeposit?.depositAmount;
			case PaymentOption.PayInFull:
				return renewal.renewalRatingResponse?.annualPremium;
			default:
				return undefined;
		}
	};

	return {
		handleStripePaymentSubmit,
		handleBraintreePaymentSubmit,
		submitting,
		problem,
		setProblem,
		RenewalPaymentError: error,
	};
};

export default useHandleRenewalPayment;
